// components/invoice/PartialPaymentDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Input, Button, Select, Dialog, TextEditor, Datepicker } from "@components/index";
import { fetchJson } from "@utils/fetchJson";
import { API_BASE_URL } from "@config";
import { useReceiptPartialStore } from "../store/useReceiptPartialStore";
import DatePicker from "react-datepicker";
import { getPlainTextFromHtml } from "@utils/getPlainTextFromHtml";

const toDDMMYYYY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
};
const toISO = (ddmmyyyy) => {
  if (!ddmmyyyy?.includes("-")) return "";
  const [d, m, y] = ddmmyyyy.split("-");
  return `${y}-${m}-${d}`;
};

// Adjust this if your endpoint is without the /E prefix
const PARTIAL_PAYMENT_ENDPOINT = `${API_BASE_URL}/receipt/partial-pay`;

export default function PartialPaymentDialog({onSuccess }) {
  const { isOpen, payload, close } = useReceiptPartialStore();
  
  const [pending_amount, setPendingAmount] = useState(parseFloat(payload?.pending_amount ?? 0)); // or 0 / initialPending
  const {
    record_id,
    customer_id,
    companyId,
  } = payload || {};
  const [activeTab, setActiveTab] = useState("form"); 
  const paymentModes = [
  { key: "CASH", value: "Cash" },
  { key: "BANK", value: "Bank Transfer" },
  { key: "UPI", value: "UPI" },
  { key: "CHEQUE", value: "Cheque" },
  { key: "CARD", value: "Card" },
  { key: "OTHER", value: "Other" },
];
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const defaultForm = {
  amount: "",
  transaction_date: todayIso,
  payment_mode: paymentModes[0]?.key ?? "",
  transaction_ref: "",
  payment_note: "",
};
  const [form, setForm] = useState(defaultForm);
const paymentModeOptions = paymentModes.map(m => ({ value: m.id, label: m.label }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // hydrate default date if passed
//   useEffect(() => {
//     if (transaction_date) {
//       setForm((f) => ({
//         ...f,
//         paymentDate: toISO(transaction_date) || f.paymentDate,
//       }));
//     }
//   }, [transaction_date]);
const getLatestLog = (arr = []) => {
  if (!arr.length) return null;
  // Prefer highest allocation_id; tie-break by allocatedAt
  return arr.reduce((latest, log) => {
    const a = Number(latest?.allocation_id) || 0;
    const b = Number(log?.allocation_id) || 0;
    if (b !== a) return b > a ? log : latest;

    // tie-break: allocatedAt (normalize "YYYY-MM-DD HH:mm:ss" to ISO-ish)
    const tA = Date.parse(String(latest?.allocatedAt || "").replace(" ", "T")) || 0;
    const tB = Date.parse(String(log?.allocatedAt || "").replace(" ", "T")) || 0;
    return tB > tA ? log : latest;
  });
};
 

  // fetch logs
  const loadLogs = async () => {
    if (!record_id) return;
    try {
      setLoadingLogs(true);
      setError("");
      const res = await fetchJson(`${API_BASE_URL}/receipt/pay-logs/${record_id}`);
      setLogs(res?.statusCode === 200 && Array.isArray(res?.data) ? res.data : []);
      const latest = getLatestLog(res.data);
        if (latest) {
          setPendingAmount(Number(latest.pendingAmt) || 0);
        }
    } catch {
      setError("Couldn't load payment history. Please try again.");
    } finally {
      setLoadingLogs(false);
    }
  };
  useEffect(() => {
    setPendingAmount(parseFloat(payload?.pending_amount));
  },[payload]);
  useEffect(() => {
    if (isOpen && record_id) loadLogs();
  }, [isOpen, record_id]);

  const typedAmt = parseFloat(form.amount || "0") || 0;
  const newPending = Math.max((parseFloat(pending_amount) || 0) - typedAmt, 0);

  const validate = () => {
    if (!form.amount || typedAmt <= 0) return "Enter a valid amount.";
    if (typedAmt > (parseFloat(pending_amount) || 0)) return "Amount exceeds pending.";
    if (!form.transaction_date) return "Select a payment date.";
    if (form.payment_mode === "" || form.payment_mode === null || form.payment_mode === undefined)
      return "Select a payment mode.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    const v = validate();
    if (v) return setError(v);

    const payloadReq = {
      customer_id: customer_id,
      company_id: companyId,
      payment_note: (form.payment_note || "").trim(),
      invoiceID: record_id,
      action: "savePayment",
      transaction_date: form.transaction_date,
      amount: Number(typedAmt).toFixed(2),
      transaction_ref: (form.transaction_ref || "").trim(),
      payment_mode: form.payment_mode,
    };
    try {
      setSubmitting(true);
      const res = await fetchJson(PARTIAL_PAYMENT_ENDPOINT, {
        method: "POST",
        body: JSON.stringify(payloadReq),
        headers: { "Content-Type": "application/json" },
      });
      const ok = res?.flag === "S" || res?.statusCode === 200;
      if (ok) {
        setInfo("Payment saved successfully.");
        await loadLogs();
        // reset amount/tx/note; keep date & mode
        setForm((f) => ({ ...f, amount: "", transaction_ref: "", payment_note: "",payment_mode:paymentModes[0]?.key }));
        onSuccess?.(res);
      } else {
        setError(res?.msg || "Failed to save payment.");
      }
    } catch {
      setError("Server error while saving payment.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeAndReset = () => {
    setForm(defaultForm);
    setError("");
    setInfo("");
    setLogs([]);
    close();
  };

if (!record_id) {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={close}
      variant="custom"
      size="xl"
    >
      <div className="p-6 text-center text-slate-600">
        No invoice found. Please select a valid invoice to record payment.
      </div>
    </Dialog>
  );
}
  return (
    <Dialog
      isOpen={isOpen}
      onClose={closeAndReset}
      variant="custom"
      size="xl"
    >
      {/* Header */}
      <div className="sticky top-0 bg-white px-5 pt-4 pb-3 border-b z-10">
        <h3 className="text-lg font-semibold">Record Payment Against Invoice #{record_id}</h3>
        <p className="text-xs text-slate-500">
          Pending: <span className="font-medium">₹{Number(pending_amount || 0).toFixed(2)}</span>
        </p>
      </div>
      <div className="px-5 border-b bg-white sticky top-[54px] z-10 flex gap-4">
  <button
    className={`py-2 px-3 text-sm font-medium ${
      activeTab === "form" ? "text-blue-600 border-b-2 border-blue-600" : "text-slate-500"
    }`}
    onClick={() => setActiveTab("form")}
  >
    Add Payment
  </button>

  <button
    className={`py-2 px-3 text-sm font-medium ${
      activeTab === "history" ? "text-blue-600 border-b-2 border-blue-600" : "text-slate-500"
    }`}
    onClick={() => setActiveTab("history")}
  >
    Payment History
  </button>
</div>
      {activeTab === "form" && (
  <>
      <form className="px-5 py-4 pb-0 space-y-4 overflow-y-auto max-h-[70vh]">
        {/* ---- Form Fields (ADDED) ---- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Payment Amount */}
          <div>
            <label className="text-xs text-slate-600">Payment Amount</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="0.00"
              required
            />
            <div className="mt-1 text-[11px] text-slate-500">
              New pending after save: <span className="font-medium">₹{newPending.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Date */}
          <div>
            <label className="text-xs text-slate-600">Transaction Date</label>
            {/* selected={from}
                      onChange={(date) => setLogDate(date)} */}
             <DatePicker
                      showMonthDropdown
                      showYearDropdown
                       selected={form.transaction_date}
                        onChange={(date) => setForm((f) => ({ ...f, transaction_date: date ?? "" }))}
                      className="ws-date form-input w-full text-gray-600 bg-gray-100 rounded focus:outline-none px-3 py-2 pr-10 px-2 py-1 text-sm"
                      placeholderText="Select a date"
                      dateFormat="dd-MM-yyyy"
                      popperClassName="z-[9999]"
                    />
          </div>

          {/* Mode of Payment */}
          <div>
            <label className="text-xs text-slate-600">Mode of Payment</label>
              <Select
              id="payment_mode"
              options={paymentModes}
              value={form.payment_mode}
              onChange={(e) => {
                const value = e.target?.value ?? e.currentTarget?.value ?? "";
                setForm(f => ({ ...f, payment_mode: value }));
              }}
              placeholder="Select mode"
              />
              
          </div>

          {/* Transaction ID */}
          <div>
            <label className="text-xs text-slate-600">Transaction ID / Details</label>
            <Input
              value={form.transaction_ref}
              onChange={(e) =>
                setForm((f) => ({ ...f, transaction_ref: e.target.value }))
              }
              placeholder="e.g., UTR/Ref #"
            />
          </div>
        </div>
      </form>
        {/* payment_note */}
        <div className="px-5 py-4">
          <label className="text-xs text-slate-600">Payment Note</label>
          <TextEditor
          showEditorInitially={true}
            value={form.payment_note}
            onChange={(val) => setForm((f) => ({ ...f, payment_note: val }))} // if your TextEditor returns a string
          />
        </div>
        {/* ---- /Form Fields ---- */}</>
              )}
        
        {activeTab === "history" && (
        <div className="mt-4 min-h-[400px]">
          <h4 className="text-sm font-semibold mb-2">Payment History</h4>
          {loadingLogs ? (
            <div>Loading…</div>
          ) : logs?.length ? (
            <table className="min-w-full text-sm border">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Receipt #</th>
                  <th className="px-3 py-2">Mode</th>
                  <th className="px-3 py-2 text-right">Paid</th>
                  <th className="px-3 py-2 text-right">Pending</th>
                  <th className="px-3 py-2">payment_note</th>
                  <th className="px-3 py-2">Tx ID</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((r) => (
                  <tr key={r.receipt_id} className="border-t">
                    <td className="px-3 py-2">{r.receipt_date}</td>
                    <td className="px-3 py-2">{r.receipt_number}</td>
                    <td className="px-3 py-2">{r.paymentMode || r.payment_mode}</td>
                    <td className="px-3 py-2 text-right">
                      ₹{Number(r.paidAmt ?? r.amount ?? 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      ₹{Number(r.pendingAmt ?? r.pending_amount ?? 0).toFixed(2)}
                    </td>
                    <td title={getPlainTextFromHtml(r.notes)} className="px-3 py-2 max-w-[200px] line-clamp-2">{getPlainTextFromHtml(r.notes) || "-"}</td>
                    <td className="px-3 py-2">{r.transaction_ref || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div>No partial payments yet.</div>
          )}
        </div>)}
        {/* Footer buttons */}
        <div className="sticky bottom-0 bg-white pt-3 pb-2 border-t -mx-5 px-5 flex justify-end gap-2">
          {error && <div className="text-sm mt-2 text-red-600">{error}</div>}
          {info && <div className="text-sm  mt-2 text-green-600">{info}</div>}
          <Button type="button" variant="secondary" onClick={closeAndReset}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </Button>
        </div>
     
    </Dialog>
  );
}
